import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { Checkbox } from '@components/atoms/Checkbox';
import {
  EquipmentAsset,
  EquipmentReturnInput,
  TaskEquipment,
  TaskEquipmentReturnInput,
  EquipmentCondition,
} from '@models/warehouse.model';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import ConstructionIcon from '@mui/icons-material/Construction';
import AssignmentIcon from '@mui/icons-material/Assignment';

/** The modal supports two modes:
 *  1. TaskEquipment mode (from InventoryTab) — equipment is TaskEquipment, onReturn receives TaskEquipmentReturnInput
 *  2. EquipmentAsset mode (from warehouse pages) — equipment is EquipmentAsset, onReturn receives EquipmentReturnInput
 */
type TaskEquipmentMode = {
  equipment: TaskEquipment | null;
  onReturn: (input: TaskEquipmentReturnInput) => Promise<void>;
};

type AssetMode = {
  equipment: EquipmentAsset | null;
  onReturn: (input: EquipmentReturnInput) => Promise<void>;
};

interface BaseProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  isDelegated?: boolean;
}

type EquipmentReturnModalProps = BaseProps & (TaskEquipmentMode | AssetMode);

const CONDITION_OPTIONS: { value: EquipmentCondition; label: string }[] = [
  { value: EquipmentCondition.GOOD, label: 'Good' },
  { value: EquipmentCondition.FAIR, label: 'Fair' },
  { value: EquipmentCondition.NEEDS_REPAIR, label: 'Needs Repair' },
];

/** Type guard: check if equipment is a TaskEquipment (has taskEquipmentId) */
const isTaskEquipment = (eq: TaskEquipment | EquipmentAsset): eq is TaskEquipment =>
  'taskEquipmentId' in eq;

export const EquipmentReturnModal: React.FC<EquipmentReturnModalProps> = ({
  isOpen,
  onClose,
  onReturn,
  equipment,
  isLoading = false,
  isDelegated,
}) => {
  const [usageAmount, setUsageAmount] = useState('');
  const [costPerHour, setCostPerHour] = useState<number | null>(null);
  const [chargeToClient, setChargeToClient] = useState(false);
  const [pricePerHour, setPricePerHour] = useState<number | null>(null);
  const [condition, setCondition] = useState<EquipmentCondition>(EquipmentCondition.GOOD);
  const [notes, setNotes] = useState('');

  // Derive rates from either TaskEquipment or EquipmentAsset
  const defaultCostPerHour = useMemo(() => {
    if (!equipment) return 0;
    if (isTaskEquipment(equipment)) {
      return equipment.costPerHour || equipment.asset?.hourlyRate || 0;
    }
    return equipment.hourlyRate || 0;
  }, [equipment]);

  const defaultClientPerHour = useMemo(() => {
    if (!equipment) return 0;
    if (isTaskEquipment(equipment)) {
      return equipment.pricePerHour || equipment.asset?.clientHourlyRate || 0;
    }
    return equipment.clientHourlyRate || 0;
  }, [equipment]);

  // Pre-fill chargeToClient from existing TaskEquipment data
  const existingChargeToClient = useMemo(() => {
    if (!equipment) return false;
    if (isTaskEquipment(equipment)) return equipment.chargeToClient || false;
    return false;
  }, [equipment]);

  // Reset form on open, auto-fill rates
  useEffect(() => {
    if (isOpen) {
      setUsageAmount('');
      setCostPerHour(defaultCostPerHour > 0 ? defaultCostPerHour : null);
      setChargeToClient(existingChargeToClient);
      setPricePerHour(defaultClientPerHour > 0 ? defaultClientPerHour : null);
      setCondition(EquipmentCondition.GOOD);
      setNotes('');
    }
  }, [isOpen, defaultCostPerHour, defaultClientPerHour, existingChargeToClient]);

  // For EquipmentAsset mode, check if it's a delegated task
  const isDelegatedTask = isDelegated || (
    equipment && !isTaskEquipment(equipment) && !!equipment.currentTask?.originalTaskId
  );

  // Parsed values
  const parsedCostPerHour = costPerHour || 0;
  const parsedPricePerHour = pricePerHour || 0;
  const parsedHours = parseFloat(usageAmount) || 0;

  // Cost/Price summary
  const summary = useMemo(() => {
    const totalCost = parsedHours * parsedCostPerHour;
    const totalPrice = parsedHours * parsedPricePerHour;
    const profit = totalPrice - totalCost;
    return { hours: parsedHours, totalCost, totalPrice, profit };
  }, [parsedHours, parsedCostPerHour, parsedPricePerHour]);

  const handleSubmit = async () => {
    if (!equipment) return;

    if (isTaskEquipment(equipment)) {
      // TaskEquipment mode
      await (onReturn as (input: TaskEquipmentReturnInput) => Promise<void>)({
        taskEquipmentId: equipment.taskEquipmentId,
        hoursUsed: summary.hours > 0 ? summary.hours : undefined,
        condition,
        notes: notes.trim() || undefined,
        costPerHour: parsedCostPerHour > 0 ? parsedCostPerHour : undefined,
        pricePerHour: chargeToClient && parsedPricePerHour > 0 ? parsedPricePerHour : undefined,
        chargeToClient,
      });
    } else {
      // EquipmentAsset mode
      await (onReturn as (input: EquipmentReturnInput) => Promise<void>)({
        assetId: equipment.assetId,
        hoursUsed: summary.hours > 0 ? summary.hours : undefined,
        condition,
        notes: notes.trim() || undefined,
        chargeToClient,
        costPerHour: summary.hours > 0 && parsedCostPerHour > 0 ? parsedCostPerHour : undefined,
        pricePerHour: summary.hours > 0 && parsedPricePerHour > 0 ? parsedPricePerHour : undefined,
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  // Get display info from either type
  const equipmentName = equipment?.name || '';
  const equipmentDetail = (() => {
    if (!equipment) return '';
    if (isTaskEquipment(equipment)) {
      const asset = equipment.asset;
      if (!asset) return equipment.description || '';
      return [
        asset.serialNumber || asset.assetCode || '',
        asset.manufacturer || '',
        asset.model || '',
      ].filter(Boolean).join(' - ');
    }
    // EquipmentAsset
    return [
      equipment.serialNumber || equipment.assetCode || '',
      equipment.manufacturer || '',
      equipment.model || '',
    ].filter(Boolean).join(' - ');
  })();

  // Current task info (only for EquipmentAsset mode)
  const currentTask = equipment && !isTaskEquipment(equipment) ? equipment.currentTask : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Return Equipment"
      confirmText="Return Equipment"
      handleConfirm={handleSubmit}
      isLoading={isLoading}
      size="medium"
    >
      <div className="flex flex-col gap-5">
        {/* Equipment Info */}
        {equipment && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <ConstructionIcon className="text-green-600" sx={{ fontSize: 20 }} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{equipmentName}</p>
                {equipmentDetail && (
                  <p className="text-xs text-gray-500 truncate">{equipmentDetail}</p>
                )}
              </div>
            </div>

            {/* Current Task Info — only in EquipmentAsset mode */}
            {currentTask && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center gap-1.5 mb-1">
                  <AssignmentIcon className="text-gray-400" sx={{ fontSize: 14 }} />
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Checked out to</p>
                </div>
                <p className="text-sm font-medium text-gray-900">{currentTask.name}</p>
                {currentTask.project && (
                  <p className="text-xs text-gray-500">{currentTask.project.name}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Hours Used & Cost Per Hour — side by side */}
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Hours Used" htmlFor="returnUsage">
            <Input
              id="returnUsage"
              type="number"
              step="0.5"
              min="0"
              value={usageAmount}
              onChange={(e) => setUsageAmount(e.target.value)}
              placeholder="0"
            />
          </FormGroup>

          <FormGroup label="Cost Per Hour" htmlFor="returnCostPerHour">
            <InputCurrency
              id="returnCostPerHour"
              value={costPerHour}
              onChange={(val) => setCostPerHour(val)}
              placeholder="0.00"
            />
            {defaultCostPerHour > 0 && parsedCostPerHour !== defaultCostPerHour && (
              <p className="text-xs text-gray-400 mt-1">
                Default: {formatCurrency(defaultCostPerHour)}/hr
              </p>
            )}
          </FormGroup>
        </div>

        {/* Cost Summary — right below the inputs */}
        {summary.hours > 0 && (parsedCostPerHour > 0 || (chargeToClient && parsedPricePerHour > 0)) && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Cost Summary</h4>
            {parsedCostPerHour > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Equipment Cost ({summary.hours}h x {formatCurrency(parsedCostPerHour)})
                </span>
                <span className="font-medium text-gray-900">{formatCurrency(summary.totalCost)}</span>
              </div>
            )}
            {chargeToClient && parsedPricePerHour > 0 && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    Client Charge ({summary.hours}h x {formatCurrency(parsedPricePerHour)})
                  </span>
                  <span className="font-medium text-green-700">{formatCurrency(summary.totalPrice)}</span>
                </div>
                {parsedCostPerHour > 0 && (
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 font-medium">Profit</span>
                      <span className={`font-semibold ${summary.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(summary.profit)}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Charge to Client — for delegated tasks or when already marked */}
        {(isDelegatedTask || existingChargeToClient) && (
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
            <div>
              <p className="text-sm font-medium text-gray-900">Charge to Client</p>
              <p className="text-xs text-gray-500">
                {chargeToClient
                  ? 'Equipment usage will be billed to the client'
                  : 'Internal cost only — not billed to client'
                }
              </p>
            </div>
            <Checkbox
              checked={chargeToClient}
              onChange={(checked) => setChargeToClient(checked)}
              name="chargeToClient"
            />
          </div>
        )}

        {/* Client Price Per Hour — only when chargeToClient is checked */}
        {chargeToClient && (
          <FormGroup label="Client Price Per Hour" htmlFor="returnPricePerHour">
            <InputCurrency
              id="returnPricePerHour"
              value={pricePerHour}
              onChange={(val) => setPricePerHour(val)}
              placeholder="0.00"
            />
            {defaultClientPerHour > 0 && parsedPricePerHour !== defaultClientPerHour && (
              <p className="text-xs text-gray-400 mt-1">
                Default: {formatCurrency(defaultClientPerHour)}/hr
              </p>
            )}
          </FormGroup>
        )}

        {/* Condition */}
        <FormGroup label="Condition After Use" htmlFor="returnCondition">
          <ReactSelect
            options={CONDITION_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
            value={{
              value: condition,
              label: CONDITION_OPTIONS.find((opt) => opt.value === condition)?.label || '',
            }}
            onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
              if (option && !Array.isArray(option) && 'value' in option) {
                setCondition(option.value as EquipmentCondition);
              }
            }}
            placeholder="Select condition..."
          />
        </FormGroup>

        {/* Notes */}
        <FormGroup label="Notes" htmlFor="returnNotes">
          <textarea
            id="returnNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any observations about the equipment condition..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            rows={3}
          />
        </FormGroup>

        {/* Warning for needs repair */}
        {condition === EquipmentCondition.NEEDS_REPAIR && (
          <div className="p-3 bg-amber-50 rounded-md border border-amber-200">
            <p className="text-sm text-amber-700">
              This equipment will be flagged for maintenance review after return.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EquipmentReturnModal;
